import React, { useEffect, useState } from 'react';
import { apiConstants, getGeneric } from '../../common/constants';
import { convertToCSV, downloadCSV, formatDate, parseDateAndTime, formatCurrency } from '../../helpers/UtilityHelper';
import ProzoDataGrid from '../../common/ProzoDataGrid';
import { Button, Card, CardContent, CardHeader, Chip, Grid, Typography, Box } from '@mui/material';
import Loader from '../../common/Loader';
import { MerchantFilterCached } from '../../common/ProshipFormComponents';
import { FormProvider, RHFDateRangePickerField } from '../../common/hook-form';
import { LoadingButton } from '@mui/lab';
import { useForm } from 'react-hook-form';
import { Download } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { WalletTypeChip } from './walletUi';

const headers = [
    { label: 'Date', key: 'time' },
    { label: 'Wallet', key: 'walletType' },
    { label: 'AWB', key: 'awb' },
    { label: 'Debit', key: 'debitAmount' },
    { label: 'Credit', key: 'creditAmount' },
    { label: 'Description', key: 'remark' },
    { label: 'Running Balance', key: 'runningBalance' },
    { label: 'Balance Meaning', key: 'balanceMeaning' },
];

/**
 * Cross-wallet statement (GET /api/wallet/statement): one chronological ledger spanning BOTH the
 * Prepaid and Postpaid eras of a merchant. WALLET_SWITCHED boundary rows arrive marked
 * (marker=true); runningBalance is labelled per row as BALANCE (merchant's money) or DUE (owed).
 */
const WalletStatement = () => {
    const [rows, setRows] = useState([]);
    const [page, setPage] = useState(0);
    const [sizePerPage, setSizePerPage] = useState(50);
    const [loading, setLoading] = useState(false);
    const [btnLoading, setBtnLoading] = useState(false);
    // bumped on every Search so the single [page, sizePerPage, searchKey] effect is the only
    // fetch trigger — calling loadStatement() directly from onSubmit raced the setPage(0)
    // effect and could paint a stale-offset response over page 1
    const [searchKey, setSearchKey] = useState(0);
    const { enqueueSnackbar } = useSnackbar();

    const methods = useForm({
        defaultValues: {
            merchant: '',
            date_range: { fromDate: '', toDate: '' },
        },
    });
    const { handleSubmit, reset, getValues } = methods;

    const buildUrl = (limit, offset) => {
        const { merchant, fromDate, toDate } = getValues();
        return `${apiConstants.WALLET_STATEMENT}?from_date=${formatDate(fromDate, 'yyyy-MM-dd')}&to_date=${formatDate(toDate, 'yyyy-MM-dd')}&merchantId=${merchant}&limit=${limit}&offset=${offset}`;
    };

    const loadStatement = () => {
        const { merchant, fromDate, toDate } = getValues();
        if (!(merchant && fromDate && toDate)) return;
        setLoading(true);
        getGeneric(buildUrl(sizePerPage, page * sizePerPage))
            .then((response) => setRows(response?.data || []))
            .catch(() => enqueueSnackbar('Failed to load statement. Please try again.', { variant: 'error' }))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        loadStatement();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, sizePerPage, searchKey]);

    const onSubmit = (data) => {
        if (data.merchant && data.fromDate && data.toDate) {
            setPage(0);
            setSearchKey((k) => k + 1);
        } else {
            enqueueSnackbar('Please select merchant and date range to view the statement.', { variant: 'error' });
        }
    };

    const downloadStatement = () => {
        const { merchant, fromDate, toDate } = getValues();
        if (!(merchant && fromDate && toDate)) return;
        setBtnLoading(true);
        // backend caps limit at 1000 per request
        getGeneric(buildUrl(1000, 0)).then((response) => {
            const data = response?.data?.length > 0 ? response.data : [];
            // a finance export must never truncate silently — 1000 returned rows means the
            // window likely has more
            if (data.length >= 1000) {
                enqueueSnackbar('Export capped at 1,000 rows — narrow the date range for a complete statement.', { variant: 'warning' });
            }
            // remarks are free text and the CSV helper does not quote fields — a comma would
            // shift every following column of the row
            const safe = data.map((r) => ({ ...r, awb: r.awb || '', remark: (r.remark || '').replace(/,/g, ';') }));
            const csvData = convertToCSV(safe, headers);
            downloadCSV(csvData, 'wallet-statement.csv');
        }).catch((err) => console.error(err)).finally(() => setBtnLoading(false));
    };

    const columns = [
        {
            field: 'time', headerName: 'Date', flex: 1, minWidth: 150, hideSortIcons: true,
            renderCell: ({ row }) => parseDateAndTime(row.time),
        },
        {
            field: 'walletType', headerName: 'Wallet', flex: 0.8, minWidth: 100, hideSortIcons: true, headerAlign: 'center', align: 'center',
            renderCell: ({ row }) => row.walletType ? <WalletTypeChip type={row.walletType} /> : null,
        },
        {
            // blank when the transaction is not linked to an order (recharge, manual, VAS, markers)
            field: 'awb', headerName: 'AWB', flex: 1, minWidth: 140, hideSortIcons: true,
            renderCell: ({ row }) => row.awb || '',
        },
        {
            field: 'type', headerName: 'Type', flex: 0.9, minWidth: 120, hideSortIcons: true, headerAlign: 'center', align: 'center',
            renderCell: ({ row }) => {
                if (row.marker) return <Chip size="small" color="warning" label="WALLET SWITCH" />;
                if (row.creditAmount) return <Chip size="small" color="success" label="Credit" sx={{ color: 'white' }} />;
                if (row.debitAmount) return <Chip size="small" color="error" label="Debit" sx={{ color: 'white' }} />;
                return null;
            },
        },
        {
            field: 'amount', headerName: 'Amount', flex: 0.9, minWidth: 110, hideSortIcons: true, align: 'right', headerAlign: 'right',
            renderCell: ({ row }) => {
                if (row.marker) return '—';
                const amt = row.creditAmount || row.debitAmount;
                return amt ? formatCurrency(amt) : '—';
            },
        },
        {
            field: 'remark', headerName: 'Description', flex: 2, minWidth: 260, hideSortIcons: true,
            renderCell: ({ row }) => (
                <Typography variant="body2" sx={row.marker ? { fontWeight: 700, color: 'warning.main' } : {}}>
                    {row.remark}
                </Typography>
            ),
        },
        {
            field: 'runningBalance', headerName: 'Running', flex: 1, minWidth: 140, hideSortIcons: true, align: 'right', headerAlign: 'right',
            renderCell: ({ row }) => {
                if (row.runningBalance === null || row.runningBalance === undefined) return '—';
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatCurrency(row.runningBalance)}</Typography>
                        {row.balanceMeaning && (
                            <Chip size="small" variant="outlined" label={row.balanceMeaning === 'DUE' ? 'DUE' : 'BAL'}
                                color={row.balanceMeaning === 'DUE' ? 'error' : 'success'} sx={{ height: 18, fontSize: 10 }} />
                        )}
                    </Box>
                );
            },
        },
    ];

    return (
        <>
            <Card>
                <CardHeader
                    subheader={
                        <Typography variant="caption" color="error" sx={{ fontWeight: 'bold' }}>
                            One continuous ledger across Prepaid and Postpaid wallets — wallet switches appear as marked boundary rows.
                        </Typography>
                    }
                    action={
                        <LoadingButton size="small" startIcon={<Download />} loading={btnLoading}
                            onClick={downloadStatement} variant="contained" color="warning"
                            disabled={!rows.length}>Download</LoadingButton>
                    }
                />
                <CardContent>
                    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                        <Grid container spacing={2}>
                            <Grid item xs={3} style={{ overflow: 'hidden' }}>
                                <MerchantFilterCached />
                            </Grid>
                            <Grid item xs={3}>
                                <RHFDateRangePickerField name="date_range" label="Date Range" maxDate={new Date()} />
                            </Grid>
                            <Grid item xs={4} sx={{ display: 'flex', alignItems: 'center' }}>
                                <Button type="submit" variant="contained" color="primary" size="small">Search</Button>
                                <Button sx={{ ml: 1 }} type="button" variant="contained" color="error" size="small" onClick={() => {
                                    reset({ merchant: '', date_range: { fromDate: '', toDate: '' } });
                                    setRows([]);
                                }}>Reset</Button>
                            </Grid>
                        </Grid>
                    </FormProvider>
                </CardContent>
            </Card>
            <Grid item container spacing={2} sx={{ mt: 2 }}>
                {loading && <Loader />}
                <Grid item xs={12}>
                    <ProzoDataGrid
                        columns={columns}
                        autoHeight={true}
                        rows={rows.map((row, index) => ({ ...row, id: index }))}
                        sizePerPage={sizePerPage}
                        setSizePerPage={setSizePerPage}
                        setPage={setPage}
                        page={page}
                        paginationMode="server"
                        rowCount={Number.MAX_VALUE}
                    />
                </Grid>
            </Grid>
        </>
    );
};

export default WalletStatement;
